import {
  HONEST_JOBS_JOB_DETAIL_FIELD_KEYS,
  HONEST_JOBS_RESOURCE_DETAIL_FIELD_KEYS,
} from '../constants/reentry-planning.constants';
import { mapReentryPlanJobToHonestJobsResource } from './map-reentry-plan-job.util';
import { mapReentryPlanResourceToHonestJobsResource } from './map-reentry-plan-resource.util';

export const HONEST_JOBS_WIDGET_TYPE = 'honest-jobs';
const HONEST_JOBS_RESOURCE_TYPE = 'Honest Jobs';
const REENTRY_PLAN_GROUP_TYPE_JOBS = 'jobs';

const REENTRY_PLAN_WIDGET_TYPES = new Set(['honest_jobs_jobs', 'honest_jobs_resources']);

const HONEST_JOBS_API_WIDGET_TYPES = new Set([
  HONEST_JOBS_WIDGET_TYPE,
  'honest_jobs',
  'honest_jobs_jobs',
  'honest_jobs_resources',
]);

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const isHonestJobsApiWidgetType = (widgetType) =>
  HONEST_JOBS_API_WIDGET_TYPES.has(widgetType);

export const widgetTypeRequiresReentryPlan = (widgetType) =>
  REENTRY_PLAN_WIDGET_TYPES.has(widgetType);

const normalizeCategoryKey = (value) => String(value ?? '').trim().toLowerCase();

const getGroupItems = (group) => {
  const groupType = String(group?.groupType ?? '').toLowerCase();

  if (groupType === REENTRY_PLAN_GROUP_TYPE_JOBS) {
    return Array.isArray(group.jobs) ? group.jobs : [];
  }

  return Array.isArray(group.resources) ? group.resources : [];
};

const mapGroupItemToHonestJobsResource = (group, item) => {
  const groupType = String(group?.groupType ?? '').toLowerCase();

  if (groupType === REENTRY_PLAN_GROUP_TYPE_JOBS) {
    return mapReentryPlanJobToHonestJobsResource(item);
  }

  return mapReentryPlanResourceToHonestJobsResource(item);
};

export const buildResourcesByCategory = (reentryPlan) => {
  if (!Array.isArray(reentryPlan?.groups)) {
    return {};
  }

  return reentryPlan.groups.reduce((resourcesByCategory, group) => {
    const categoryKey = normalizeCategoryKey(group?.name);
    if (!categoryKey) {
      return resourcesByCategory;
    }

    const resources = getGroupItems(group).map((item) =>
      mapGroupItemToHonestJobsResource(group, item),
    );
    return { ...resourcesByCategory, [categoryKey]: resources };
  }, {});
};

const resolveHonestJobsTitle = (config) => config.title ?? config.category ?? '';

const resolveHonestJobsResources = (config) =>
  Array.isArray(config.resources) ? config.resources : [];

const resolveHonestJobsResourcesFromReentryPlan = (config, resourcesByCategory) => {
  const categoryKey = normalizeCategoryKey(config.category ?? config.title);
  const reentryResources = resourcesByCategory[categoryKey] ?? [];

  if (reentryResources.length > 0) {
    return reentryResources;
  }

  return resolveHonestJobsResources(config);
};

const pickTruthyConfigField = (config, key) => (config[key] ? { [key]: config[key] } : {});

const pickNullableNumberConfigField = (config, key) =>
  config[key] != null ? { [key]: config[key] } : {};

const pickHonestJobsOptionalConfig = (config) => ({
  ...pickNullableNumberConfigField(config, 'initialVisibleCount'),
  ...pickTruthyConfigField(config, 'moreInfoLabel'),
  ...pickTruthyConfigField(config, 'emptyStateTitle'),
  ...pickTruthyConfigField(config, 'emptyStateDescription'),
});

const isHonestJobsJobsWidgetType = (widgetType) => widgetType === 'honest_jobs_jobs';

const resolveHonestJobsResourceOrJobDetailFieldKeys = (widgetType, config) => {
  if (Array.isArray(config.detailFieldKeys)) {
    return config.detailFieldKeys;
  }

  return isHonestJobsJobsWidgetType(widgetType)
    ? HONEST_JOBS_JOB_DETAIL_FIELD_KEYS
    : HONEST_JOBS_RESOURCE_DETAIL_FIELD_KEYS;
};

export const mapHonestJobsWidgetProps = (widget, resourcesByCategory = {}) => {
  const config = isPlainObject(widget?.config) ? widget.config : {};
  const widgetType = widget?.widgetType ?? '';

  return {
    title: resolveHonestJobsTitle(config),
    resources: resolveHonestJobsResourcesFromReentryPlan(config, resourcesByCategory),
    resourceType: config.resourceType ?? HONEST_JOBS_RESOURCE_TYPE,
    detailFieldKeys: resolveHonestJobsResourceOrJobDetailFieldKeys(widgetType, config),
    ...pickHonestJobsOptionalConfig(config),
  };
};
